import { core } from "@tauri-apps/api";

// Types
export interface TuyaDevice {
  device_type:
    | "Camera"
    | "Curtain"
    | "Fan"
    | "Light"
    | "Lock"
    | "Other"
    | "Sensor"
    | "Switch"
    | "Thermostat";
  id: string;
  ip?: string;
  mac?: string;
  name: string;
  room?: string;
  state: boolean;
  status: "Error" | "Offline" | "Online";
  value?: number;
  version?: string;
}

export interface TuyaConfig {
  access_id: string;
  access_key: string;
}

export interface TuyaTokenResponse {
  access_token: string;
  expire_time: number;
  refresh_token: string;
  uid: string;
}

export interface TuyaHome {
  devices: TuyaDevice[];
  home_id: string;
  name: string;
}

export interface TuyaCommand {
  code: string;
  value: boolean | number | string;
}

export interface TuyaDeviceStatus {
  device_id: string;
  last_update: number;
  state: boolean;
  status: "Error" | "Offline" | "Online";
  value?: number;
}

// Auto-Refresh Types
export interface AutoRefreshConfig {
  enabled: boolean;
  interval_seconds: number;
  min_interval_seconds: number;
  rate_limit_per_second: number;
  recommended_interval_seconds: number;
}

export interface RateLimitInfo {
  current_interval_seconds: number;
  is_valid: boolean;
  min_interval_seconds: number;
  online_devices: number;
  rate_limit_per_second: number;
  recommended_interval_seconds: number;
  warning_message?: string;
}

export interface AutoRefreshStatus {
  is_active: boolean;
  last_refresh?: number;
  next_refresh?: number;
  rate_limit_info: RateLimitInfo;
  total_refreshes: number;
}

// Tuya Service Class
/**
 * TuyaService - Singleton service for managing Tuya smart home devices
 * Provides methods for device control, status monitoring, and auto-refresh functionality
 */
export class TuyaService {
  private static instance: TuyaService | undefined;
  private isInitialized = false;
  private autoRefreshInterval: NodeJS.Timeout | null = null;
  private static readonly QUOTA_BLOCK_KEY = "tuya_quota_block_until";

  /**
   * Private constructor to enforce singleton pattern
   *
   * @returns {void}
   *
   */
  private constructor() {
    // Singleton pattern - constructor is intentionally empty
  }

  /**
   * Get singleton instance of TuyaService
   *
   * @returns {TuyaService} Singleton instance
   *
   */
  public static getInstance(): TuyaService {
    TuyaService.instance ??= new TuyaService();
    return TuyaService.instance;
  }

  /**
   * Initialize Tuya client with configuration
   *
   * @param config
   *
   * @returns {Promise<TuyaTokenResponse>} Token response with access and refresh tokens
   *
   */
  async initialize(config: TuyaConfig): Promise<TuyaTokenResponse> {
    try {
      const response = await core.invoke<TuyaTokenResponse>("tuya_init", {
        config,
      });
      this.isInitialized = true;
      return response;
    } catch (error) {
      // Failed to initialize Tuya
      throw new Error(
        `Tuya initialization failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Check if Tuya is connected
   *
   * @returns {Promise<boolean>} True if connected, false otherwise
   *
   */
  async isConnected(): Promise<boolean> {
    try {
      const result = await core.invoke<boolean>("tuya_is_connected");
      return result;
    } catch {
      // Failed to check Tuya connection
      return false;
    }
  }

  /**
   * Load saved configuration
   *
   * @returns {Promise<null | TuyaConfig>} Saved config or null if not found
   *
   */
  loadConfig(): Promise<null | TuyaConfig> {
    try {
      const savedConfig = localStorage.getItem("tuya_config");
      if (savedConfig) {
        return Promise.resolve(JSON.parse(savedConfig) as TuyaConfig);
      }
      return Promise.resolve(null);
    } catch {
      // Failed to load Tuya config
      return Promise.resolve(null);
    }
  }

  /**
   * Auto-initialize if config exists
   *
   * @returns {Promise<boolean>} True if initialized, false otherwise
   *
   */
  async autoInitialize(): Promise<boolean> {
    try {
      if (this.isInitialized) {
        return true;
      }
      const config = await this.loadConfig();
      if (config) {
        await this.initialize(config);
        return true;
      }
      return false;
    } catch {
      // Failed to auto-initialize Tuya
      return false;
    }
  }

  /**
   * Get all Tuya devices
   *
   * @returns {Promise<TuyaDevice[]>} Array of Tuya devices
   *
   */
  async getDevices(): Promise<TuyaDevice[]> {
    try {
      if (!this.isInitialized) {
        throw new Error("Tuya not initialized. Call initialize() first.");
      }
      // Quota circuit breaker: skip calls if blocked
      const blockInfo = this.getQuotaBlockInfo();
      if (blockInfo.blocked && blockInfo.until !== undefined) {
        const untilString = new Date(blockInfo.until).toLocaleString();
        throw new Error(`Tuya quota temporarily blocked until ${untilString}`);
      }
      return await core.invoke<TuyaDevice[]>("tuya_get_devices");
    } catch (error) {
      // Failed to get Tuya devices
      const message =
        error instanceof Error ? error.message : (error?.toString() ?? "unknown error");
      // Detect Tuya quota exhaustion (code 28841004 or message substring)
      if (
        message.includes("28841004") ||
        message.toLowerCase().includes("trial") ||
        message.toLowerCase().includes("quota")
      ) {
        // Block further calls for 60 minutes to avoid spamming
        this.setQuotaBlock(60);
        // Stop frontend timer if any
        this.stopAutoRefreshTimer();
      }
      throw new Error(`Failed to get devices: ${message}`);
    }
  }

  /**
   * Control a Tuya device
   *
   * @param deviceId
   *
   * @param commands
   *
   * @returns {Promise<boolean>} True if command succeeded, false otherwise
   *
   */
  async controlDevice(deviceId: string, commands: TuyaCommand[]): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        throw new Error("Tuya not initialized. Call initialize() first.");
      }
      return await core.invoke<boolean>("tuya_control_device", {
        commands,
        deviceId,
      });
    } catch (error) {
      // Failed to control Tuya device
      throw new Error(
        `Failed to control device: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Toggle a device on/off
   *
   * @param deviceId
   *
   * @param turnOn
   *
   * @returns {Promise<boolean>} True if toggle succeeded, false otherwise
   *
   */
  async toggleDevice(deviceId: string, turnOn: boolean): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        throw new Error("Tuya not initialized. Call initialize() first.");
      }
      return await core.invoke<boolean>("tuya_toggle_device", {
        deviceId,
        turnOn,
      });
    } catch (error) {
      // Failed to toggle Tuya device
      throw new Error(
        `Failed to toggle device: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Get device model to find correct command codes
   *
   * @param deviceId
   *
   * @returns {Promise<{ model?: string; [key: string]: unknown }>} Device model information
   *
   */
  async getDeviceModel(deviceId: string): Promise<{ model?: string; [key: string]: unknown }> {
    try {
      if (!this.isInitialized) {
        throw new Error("Tuya not initialized. Call initialize() first.");
      }
      return await core.invoke<{ model?: string; [key: string]: unknown }>(
        "tuya_get_device_model",
        { deviceId },
      );
    } catch (error) {
      // Failed to get Tuya device model
      throw new Error(
        `Failed to get device model: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Get device properties/status
   *
   * @param deviceId
   *
   * @returns {Promise<TuyaDeviceStatus>} Device status information
   *
   */
  async getDeviceProperties(deviceId: string): Promise<TuyaDeviceStatus> {
    try {
      if (!this.isInitialized) {
        throw new Error("Tuya not initialized. Call initialize() first.");
      }
      return await core.invoke<TuyaDeviceStatus>("tuya_get_device_properties", {
        deviceId,
      });
    } catch (error) {
      // Failed to get Tuya device properties
      throw new Error(
        `Failed to get device properties: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Get smart home overview
   *
   * @returns {Promise<TuyaHome>} Home with devices
   *
   */
  async getOverview(): Promise<TuyaHome> {
    try {
      if (!this.isInitialized) {
        throw new Error("Tuya not initialized. Call initialize() first.");
      }
      return await core.invoke<TuyaHome>("tuya_get_overview");
    } catch (error) {
      // Failed to get Tuya overview
      throw new Error(
        `Failed to get overview: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Get devices by type
   *
   * @param type
   *
   * @returns {Promise<TuyaDevice[]>} Filtered array of devices
   *
   */
  async getDevicesByType(type: TuyaDevice["device_type"]): Promise<TuyaDevice[]> {
    const devices = await this.getDevices();
    return devices.filter((device) => device.device_type === type);
  }

  /**
   * Get online devices
   *
   * @returns {Promise<TuyaDevice[]>} Array of online devices
   *
   */
  async getOnlineDevices(): Promise<TuyaDevice[]> {
    const devices = await this.getDevices();
    return devices.filter((device) => device.status === "Online");
  }

  /**
   * Get devices by room
   *
   * @param room
   *
   * @returns {Promise<TuyaDevice[]>} Filtered array of devices in room
   *
   */
  async getDevicesByRoom(room: string): Promise<TuyaDevice[]> {
    const devices = await this.getDevices();
    return devices.filter((device) => device.room === room);
  }

  /**
   * Turn on all online lights
   *
   * @returns {Promise<boolean[]>} Array of success statuses for each light
   *
   */
  async turnOnAllLights(): Promise<boolean[]> {
    const devices = await this.getDevices();
    const onlineLights = devices.filter(
      (device) => device.device_type === "Light" && device.status === "Online",
    );
    const results = await Promise.allSettled(
      onlineLights.map((light) => this.toggleDevice(light.id, true)),
    );
    return results.map((result) => (result.status === "fulfilled" ? result.value : false));
  }

  /**
   * Turn off all online lights
   *
   * @returns {Promise<boolean[]>} Array of success statuses for each light
   *
   */
  async turnOffAllLights(): Promise<boolean[]> {
    const devices = await this.getDevices();
    const onlineLights = devices.filter(
      (device) => device.device_type === "Light" && device.status === "Online",
    );
    const results = await Promise.allSettled(
      onlineLights.map((light) => this.toggleDevice(light.id, false)),
    );
    return results.map((result) => (result.status === "fulfilled" ? result.value : false));
  }

  /**
   * Get temperature sensors
   *
   * @returns {Promise<TuyaDevice[]>} Array of temperature sensors
   *
   */
  async getTemperatureSensors(): Promise<TuyaDevice[]> {
    const sensors = await this.getDevicesByType("Sensor");
    return sensors.filter((sensor) => sensor.value !== undefined);
  }

  /**
   * Get average temperature
   *
   * @returns {Promise<null | number>} Average temperature or null if no sensors
   *
   */
  async getAverageTemperature(): Promise<null | number> {
    const sensors = await this.getTemperatureSensors();
    if (sensors.length === 0) {
      return null;
    }

    const temperatures = sensors
      .map((sensor) => sensor.value)
      .filter((temporary): temporary is number => temporary !== undefined);

    if (temperatures.length === 0) {
      return null;
    }

    return temperatures.reduce((sum, temporary) => sum + temporary, 0) / temperatures.length;
  }

  // Auto-Refresh Methods

  /**
   * Get auto-refresh configuration
   *
   * @returns {Promise<AutoRefreshConfig>} Auto-refresh configuration
   *
   */
  async getAutoRefreshConfig(): Promise<AutoRefreshConfig> {
    try {
      return await core.invoke<AutoRefreshConfig>("tuya_get_auto_refresh_config");
    } catch (error) {
      // Failed to get auto-refresh config
      throw new Error(
        `Failed to get auto-refresh config: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Update auto-refresh configuration
   *
   * @param config
   *
   * @returns {Promise<AutoRefreshConfig>} Updated auto-refresh configuration
   *
   */
  async updateAutoRefreshConfig(config: AutoRefreshConfig): Promise<AutoRefreshConfig> {
    try {
      return await core.invoke<AutoRefreshConfig>("tuya_update_auto_refresh_config", { config });
    } catch (error) {
      // Failed to update auto-refresh config
      throw new Error(
        `Failed to update auto-refresh config: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Get auto-refresh status
   *
   * @returns {Promise<AutoRefreshStatus>} Auto-refresh status information
   *
   */
  async getAutoRefreshStatus(): Promise<AutoRefreshStatus> {
    try {
      return await core.invoke<AutoRefreshStatus>("tuya_get_auto_refresh_status");
    } catch (error) {
      // Failed to get auto-refresh status
      throw new Error(
        `Failed to get auto-refresh status: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Start auto-refresh
   *
   * @returns {Promise<boolean>} True if started successfully, false otherwise
   *
   */
  async startAutoRefresh(): Promise<boolean> {
    try {
      // Do not start if quota is blocked
      const blockInfo = this.getQuotaBlockInfo();
      if (blockInfo.blocked) {
        // Skipping auto-refresh start: Tuya quota blocked
        return false;
      }
      const result = await core.invoke<boolean>("tuya_start_auto_refresh");
      if (result) {
        this.startAutoRefreshTimer();
      }
      return result;
    } catch (error) {
      // Failed to start auto-refresh
      throw new Error(
        `Failed to start auto-refresh: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Stop auto-refresh
   *
   * @returns {Promise<boolean>} True if stopped successfully, false otherwise
   *
   */
  async stopAutoRefresh(): Promise<boolean> {
    try {
      const result = await core.invoke<boolean>("tuya_stop_auto_refresh");
      if (result) {
        this.stopAutoRefreshTimer();
      }
      return result;
    } catch (error) {
      // Failed to stop auto-refresh
      throw new Error(
        `Failed to stop auto-refresh: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Get rate limit information
   *
   * @param onlineDevices
   *
   * @param currentInterval
   *
   * @returns {Promise<RateLimitInfo>} Rate limit information
   *
   */
  async getRateLimitInfo(onlineDevices: number, currentInterval: number): Promise<RateLimitInfo> {
    try {
      return await core.invoke<RateLimitInfo>("tuya_get_rate_limit_info", {
        currentInterval,
        onlineDevices,
      });
    } catch (error) {
      // Failed to get rate limit info
      throw new Error(
        `Failed to get rate limit info: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Refresh online devices (for auto-refresh)
   *
   * @returns {Promise<TuyaDevice[]>} Array of refreshed online devices
   *
   */
  async refreshOnlineDevices(): Promise<TuyaDevice[]> {
    try {
      return await core.invoke<TuyaDevice[]>("tuya_refresh_online_devices");
    } catch (error) {
      // Failed to refresh online devices
      throw new Error(
        `Failed to refresh online devices: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Start auto-refresh timer
   *
   * @returns {void}
   *
   */
  private startAutoRefreshTimer(): void {
    this.stopAutoRefreshTimer(); // Clear existing timer

    // NOTE: Explicitly handle async config Promise to avoid floating promises and
    //       align with linting standards without changing the public API.
    void this.getAutoRefreshConfig()
      .then((config) => {
        // Do not start if quota is blocked
        const blockInfo = this.getQuotaBlockInfo();
        if (blockInfo.blocked) {
          // Skipping auto-refresh timer: Tuya quota blocked
          return;
        }
        this.autoRefreshInterval = setInterval(() => {
          void (async () => {
            try {
              await this.refreshOnlineDevices();
            } catch {
              // Auto-refresh failed
            }
          })();
        }, config.interval_seconds * 1000);
      })
      .catch(() => {
        // NOTE: If we cannot load the config, we simply do not start a timer;
        //       this avoids hidden promise rejections without leaking errors.
      });
  }

  /**
   * Stop auto-refresh timer
   *
   * @returns {void}
   *
   */
  private stopAutoRefreshTimer(): void {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
      this.autoRefreshInterval = null;
    }
  }

  // Quota block helpers
  /**
   * Get quota block information
   *
   * @returns {{ blocked: boolean; until?: number }} Quota block status
   *
   */
  private getQuotaBlockInfo(): { blocked: boolean; until?: number } {
    try {
      const raw = localStorage.getItem(TuyaService.QUOTA_BLOCK_KEY);
      if (!raw) {
        return { blocked: false };
      }
      const until = Number.parseInt(raw, 10);
      if (Number.isFinite(until) && Date.now() < until) {
        return { blocked: true, until };
      }
      return { blocked: false };
    } catch {
      return { blocked: false };
    }
  }

  /**
   * Set quota block for specified minutes
   *
   * @param minutes - Minutes to block
   *
   * @returns {void}
   *
   */
  private setQuotaBlock(minutes: number): void {
    const until = Date.now() + minutes * 60 * 1000;
    try {
      localStorage.setItem(TuyaService.QUOTA_BLOCK_KEY, String(until));
    } catch {
      // ignore storage errors
    }
  }
}

// Export singleton instance
export const tuyaService = TuyaService.getInstance();
